// @ts-nocheck
import { DialogComponent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { CartUpdateEvent } from '@theme/events';
import {
  resolveStoreSelectorBrand,
  appendStoreSelectorBrandParam,
  buildMf10StorePayload,
  buildAmbStoreSelectionFromMf10,
  canonicalStoreSelectionBrand,
  clearCartSilently,
  syncCartAttributes,
  postMf10CustomerMetafield,
} from '@theme/utilities';
import { createTranslator } from './translator.js';
import { LocationsService } from '@theme/locations-service';
import { getPickupPromptState, PICKUP_PROMPT_NONE, PICKUP_PROMPT_OK } from '@theme/store-selection-state';

/**
 * How many of the nearest bakeries a place search must keep in frame, matching
 * the store locator (`NEAREST_LOCATIONS_TO_FRAME` in `assets/store-locator.js`).
 * The geocode bounds are expanded to include these points, then every Click &
 * Collect bakery inside that frame is listed. A hard 20km radius dropped every
 * New York bakery when "new york" resolved to a city centroid (BDH-2729).
 */
const NEAREST_BAKERIES_IN_FRAME = 3;

/**
 * Shortest query a text miss will send to the geocoder. Keeps half-typed
 * stubs ("de") from burning a geocode request and resolving to an unrelated
 * place. Postcode-shaped queries bypass this — they geocode first.
 */
const MIN_FALLBACK_GEOCODE_LENGTH = 3;

let cachedAllLocations = null;
let cachedLocationsPromise = null;

export function resetLocationsCache() {
  cachedAllLocations = null;
  cachedLocationsPromise = null;
}

export async function getCachedAllLocations(locationsService) {
  if (cachedAllLocations) {
    return cachedAllLocations.map((location) =>
      locationsService.refreshPickupAlignedLocation(location)
    );
  }
  if (!locationsService || typeof locationsService.getAllLocations !== 'function') {
    throw new Error('getCachedAllLocations: invalid locationsService');
  }
  if (!cachedLocationsPromise) {
    cachedLocationsPromise = locationsService.getAllLocations();
  }
  try {
    cachedAllLocations = await cachedLocationsPromise;
    return cachedAllLocations;
  } catch (err) {
    cachedLocationsPromise = null;
    throw err;
  }
}

class StoreSelectorDrawerComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'content',
    'triggerLabel',
    'searchSection',
    'searchChangeBakeryButton',
    'currentSelection',
    'currentSelectionCard',
    'searchInput',
    'resultsContainer',
    'entryTemplate',
    'pickupPanel',
    'pickupSlotSelect',
    'pickupUnavailableMessage',
    'saveButton',
    'confirmDialog',
    'confirmDialogMessage',
    'confirmOkButton',
    'confirmCancelButton',
  ];
  copy = {};

  constructor() {
    super();
    this.opener = null;
    this.unsubscribe = null;
    this.locationsService = null;
    this.allLocations = [];
    this.renderedLocations = [];
    this.pendingSelectionId = '';
    this.pendingSelection = null;
    /** @type {string} from `data-pending-store-id` on external open trigger (SL/SD) */
    this.pendingOpenStoreId = '';
    /** @type {boolean} from `data-ssd-focus-pickup` on the open trigger, or `openPickup()`. */
    this.pendingOpenFocusPickup = false;
    this.pendingPickupDay = '';
    this.pendingPickupSlot = '';
    this.searchDebounce = null;
    this.userLocation = null;
    /** @type {{lat: number, lng: number} | null} origin set by geocoding a postcode/zip query (kept separate from userLocation so geolocation and postcode search don't clobber each other). */
    this.searchOrigin = null;
    /** @type {AbortController | null} cancels stale in-flight geocode requests. */
    this.geoAbortController = null;
    this.loaded = false;
    this.isChangingBakery = false;
    this.translator = null;
    this.cartResyncQueued = false;
    /** @type {((accepted: boolean) => void) | null} resolves the active confirm dialog promise. */
    this.confirmResolver = null;
    /** @type {string} latest `getPickupPromptState` result for the saved selection. */
    this.pickupPromptState = PICKUP_PROMPT_NONE;
  }

  connectedCallback() {
    const existingActive = document.querySelector('store-selector-drawer-component[data-ssd-active="true"]');
    if (existingActive && existingActive !== this) {
      this.dataset.ssdShadowed = 'true';
      return;
    }
    this.dataset.ssdActive = 'true';

    super.connectedCallback();
    this.#initializeCopy();
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
    this.refs.searchInput.addEventListener('input', this.#handleSearchInput);
    this.refs.searchInput.addEventListener('keydown', this.#handleSearchKeydown);
    this.refs.searchInput.addEventListener('search', this.#handleNativeSearch);
    this.refs.resultsContainer.addEventListener('click', this.#handleResultClick);
    this.refs.resultsContainer.addEventListener('keydown', this.#handleResultKeydown);
    this.refs.pickupPanel.addEventListener('change', this.#handlePickupDayChange);
    document.addEventListener('click', this.#handleSsdHeaderTooltipCloseDocument, true);
    document.addEventListener('click', this.#handleExternalOpenTrigger);
    document.addEventListener('cart:update', this.#handleCartUpdateResync);
    document.addEventListener('theme:ssd-header-section-morphed', this.#handleSsdHeaderSectionMorphed);
    this.refs.confirmDialog.addEventListener('cancel', this.#handleConfirmDialogCancel);
    this.refs.confirmDialog.addEventListener('click', this.#handleConfirmDialogBackdropClick);
    this.refs.confirmDialog.addEventListener('keydown', this.#handleConfirmDialogKeydown);
    this.#bindSelectionUpdates();
    this.#syncPickupDayDateLabels();
    void this.#syncSelectionFromCustomerMf10().then(async () => {
      const stateApi = window.StoreSelectionState;
      const sel = stateApi && typeof stateApi.getSelection === 'function' ? stateApi.getSelection() : null;
      const needCatalog = this.#hasCurrentMyBakery(sel) && !this.loaded;
      if (!needCatalog) {
        this.#syncSelectionDisplay();
      }
      this.#updateSaveState();
      if (needCatalog) {
        await this.#ensureLocationsLoaded();
        this.#syncSelectionDisplay();
        this.#updateSaveState();
      }
      void this.#ensureCartAttributesMatchSelection();
    });
  }

  #initializeCopy() {
    if (!this.translator) {
      this.translator = createTranslator('store-selector-drawer-translations', '');
    }

    this.copy = {
      headerDefault: this.#translate('store_selector.header.default_label', ''),
      currentLabel: this.#translate('store_selector.current.label', ''),
      emptyNoResultsText: this.#translate('store_selector.empty.no_results_text', ''),
      geolocationError: this.#translate('store_selector.geolocation.error', ''),
      geolocationNotSupported: this.#translate('store_selector.geolocation.not_supported', ''),
      searching: this.#translate('store_selector.search.searching', 'Searching nearby bakeries…'),
      geocodeError: this.#translate(
        'store_selector.search.geocode_error',
        "We couldn't find that postcode. Try a suburb or bakery name."
      ),
      noResultsWithinRadius: this.#translate(
        'store_selector.search.no_results_within_radius',
        'No pick-up bakeries available around this area.'
      ),
      noResultsWithinRadiusSignup: this.#translate('store_selector.search.no_results_within_radius_signup', 'Sign up'),
      // The programme name lives in the suffix, so this fallback forks on brand:
      // a store whose locale file predates the key must not offer COBS shoppers
      // the Bakers programme.
      noResultsWithinRadiusSignupSuffix: this.#translate(
        'store_selector.search.no_results_within_radius_signup_suffix',
        this.dataset.brand === 'cobs'
          ? 'to COBS CLUB to stay tuned to any bakery news'
          : 'to Dough Getters to stay tuned to any bakery news'
      ),
      statusOpen: this.#translate('location.status.open', ''),
      statusClosed: this.#translate('location.status.closed', ''),
      statusTemporarilyClosed: this.#translate('location.status.temporarily_closed', ''),
      saveSuccess: this.#translate('store_selector.messages.save_success', ''),
      confirmMetafieldUpdateChange: this.#translate('store_selector.messages.confirm_metafield_update_change', ''),
      confirmMetafieldUpdateChangeWithCart: this.#translate(
        'store_selector.messages.confirm_metafield_update_change_with_cart',
        ''
      ),
      metafieldUpdateFailed: this.#translate('store_selector.messages.metafield_update_failed', ''),
      selectAsMyBakery: this.#translate('store_selector.actions.select_as_my_bakery', 'Select as my bakery'),
      pickupBothDaysUnavailable: this.#translate(
        'store_selector.pickup.both_days_unavailable',
        'This bakery has no pickup availability for today or tomorrow. Please choose another bakery.'
      ),
      pickupToday: this.#translate('store_selector.pickup.today', 'Today'),
      pickupTomorrow: this.#translate('store_selector.pickup.tomorrow', 'Tomorrow'),
      pickupSelectTime: this.#translate('store_selector.pickup.select_pickup_time', 'Select a pickup time'),
    };
  }

  #translate(key, fallback) {
    if (!this.translator) return fallback;
    const translated = this.translator.translate(key);
    if (typeof translated !== 'string') return fallback;
    if (translated === key) return fallback;
    const trimmed = translated.trim();
    if (!trimmed) return fallback;
    // Shopify's `| t` filter renders absent keys as the literal string
    // `Translation missing: <locale>.<key>`. That string IS non-empty and
    // IS different from the key, so the checks above wouldn't catch it —
    // and without this branch it would render verbatim on the storefront
    // (the exact bug reported on the confirm-dialog buttons). Detect the
    // sentinel and fall back to the caller-supplied default instead.
    if (/^translation missing/i.test(trimmed)) return fallback;
    return translated;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncPickupDayDateLabels();
    this.#syncSelectionDisplay();
    this.#updateSaveState();
  }

  disconnectedCallback() {
    if (this.dataset.ssdShadowed === 'true') return;

    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.refs.searchInput.removeEventListener('input', this.#handleSearchInput);
    this.refs.searchInput.removeEventListener('keydown', this.#handleSearchKeydown);
    this.refs.searchInput.removeEventListener('search', this.#handleNativeSearch);
    this.refs.resultsContainer.removeEventListener('click', this.#handleResultClick);
    this.refs.resultsContainer.removeEventListener('keydown', this.#handleResultKeydown);
    this.refs.pickupPanel.removeEventListener('change', this.#handlePickupDayChange);
    document.removeEventListener('click', this.#handleSsdHeaderTooltipCloseDocument, true);
    document.removeEventListener('click', this.#handleExternalOpenTrigger);
    document.removeEventListener('cart:update', this.#handleCartUpdateResync);
    document.removeEventListener('theme:ssd-header-section-morphed', this.#handleSsdHeaderSectionMorphed);
    this.refs.confirmDialog.removeEventListener('cancel', this.#handleConfirmDialogCancel);
    this.refs.confirmDialog.removeEventListener('click', this.#handleConfirmDialogBackdropClick);
    this.refs.confirmDialog.removeEventListener('keydown', this.#handleConfirmDialogKeydown);

    if (typeof this.unsubscribe === 'function') {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    if (this.searchDebounce) {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }

    delete this.dataset.ssdActive;
    super.disconnectedCallback();
  }

  #runDeferredSelectionDisplayResync() {
    this.#syncSelectionDisplay();
    this.#updateSaveState();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncSelectionDisplay();
        this.#updateSaveState();
      });
    });
  }

  #handleCartUpdateResync = (event) => {
    const source = String(event?.detail?.data?.source || '');
    if (source === 'store-selector-drawer' || source === 'store-selector-drawer-after-close') return;
    if (this.cartResyncQueued) return;
    this.cartResyncQueued = true;
    queueMicrotask(() => {
      this.cartResyncQueued = false;
      this.#runDeferredSelectionDisplayResync();
    });
  };

  #handleSsdHeaderSectionMorphed = () => {
    if (document.getElementById('store-selector-drawer-root') !== this) return;
    queueMicrotask(() => {
      this.#runDeferredSelectionDisplayResync();
    });
  };

  #handleExternalOpenTrigger = (event) => {
    const rootDrawer = document.getElementById('store-selector-drawer-root');
    if (rootDrawer !== this) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('.js-open-store-selector-drawer,[data-open-store-selector-drawer]');
    if (!trigger) return;
    event.preventDefault();
    this.open(trigger instanceof HTMLElement ? trigger : null);
  };

  /**
   * @param {HTMLElement | null} [openerElement] click target (e.g. SL/SD make-my-store with `data-pending-store-id`)
   */
  open(openerElement = null) {
    const activeElement = document.activeElement;
    const opener =
      openerElement instanceof HTMLElement
        ? openerElement
        : activeElement instanceof HTMLElement
          ? activeElement
          : null;
    this.opener = opener;
    const pendingId = opener?.dataset?.pendingStoreId || opener?.getAttribute('data-pending-store-id') || '';
    this.pendingOpenStoreId = String(pendingId || '').trim();
    if (opener?.hasAttribute?.('data-ssd-focus-pickup')) this.pendingOpenFocusPickup = true;
    this.showDialog();
  }

  /**
   * Open with the saved bakery already pending and focus on the "When" panel, for
   * surfaces prompting a shopper whose selection is missing (or has outlived) its
   * collection time. Unlike the `data-pending-store-id` handoff this preserves the
   * saved pickup day, so only the stale half needs re-choosing.
   *
   * @param {HTMLElement | null} [openerElement]
   */
  openPickup(openerElement = null) {
    this.pendingOpenFocusPickup = true;
    this.open(openerElement);
  }

  close() {
    this.closeDialog();
  }

  #handleDialogOpen = () => {
    trapFocus(this.refs.dialog);
    this.refs.dialog.scrollTop = 0;
    if (this.refs.content instanceof HTMLElement) this.refs.content.scrollTop = 0;
    this.isChangingBakery = false;
    const externalPendingId = String(this.pendingOpenStoreId || '').trim();
    this.pendingOpenStoreId = '';
    const focusPickup = this.pendingOpenFocusPickup;
    this.pendingOpenFocusPickup = false;
    if (externalPendingId) {
      this.pendingSelectionId = externalPendingId;
      this.pendingSelection = this.#findLocationByStoreId(externalPendingId);
      this.pendingPickupDay = '';
      this.pendingPickupSlot = '';
      void this.#ensureLocationsLoaded().then(() => {
        if (this.refs.dialog?.open && this.pendingSelectionId === externalPendingId) {
          this.#setPendingSelection(externalPendingId);
          this.#focusPendingPickupAfterStoreSelect();
        }
      });
    } else {
      this.#hydratePendingFromSavedSelection();
      const locationsReady = this.#ensureLocationsLoaded();
      if (focusPickup) {
        // The saved bakery only resolves against the catalog, which `#hydratePendingFromSavedSelection`
        // may have run before, so wait for it rather than focusing an empty panel.
        void Promise.resolve(locationsReady).then(() => {
          if (!this.refs.dialog?.open) return;
          if (this.pendingSelection) {
            this.#focusPendingPickupAfterStoreSelect();
          } else {
            this.#focusDefaultOnOpen();
          }
        });
      }
    }
    this.#syncSelectionDisplay();
    this.#resetSearchFeedback();
    if (externalPendingId || focusPickup) {
      return;
    }
    this.#focusDefaultOnOpen();
  };

  #focusDefaultOnOpen() {
    if (this.refs.searchSection.classList.contains('hidden')) {
      this.refs.searchChangeBakeryButton.focus();
    } else {
      this.refs.searchInput.focus();
    }
  }

  #handleDialogClose = () => {
    this.#resetDrawerStateOnClose();
    removeTrapFocus();
    if (this.opener instanceof HTMLElement) {
      this.opener.focus();
      this.opener = null;
    }
  };

  #bindSelectionUpdates() {
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.subscribe !== 'function') return;
    this.unsubscribe = stateApi.subscribe(() => this.#syncSelectionDisplay());
  }

  #syncSelectionDisplay() {
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.getSelection !== 'function') {
      this.dataset.hasSelection = 'false';
      this.#applyPickupPromptState(null);
      this.#setTriggerLabel(this.#headerDefaultLabel());
      this.#setCurrentSelectionHidden(true);
      this.#applySearchStageVisibility(false);
      this.#syncTriggerTooltipVisibility(false);
      this.#syncCartSelectionSummary(null);
      this.#refreshResultsCurrentBadge();
      return;
    }

    const selection = stateApi.getSelection();
    const hasSaved = this.#hasCurrentMyBakery(selection);
    const showPendingPreview = this.#shouldShowPendingCurrentSelectionPreview();

    if (!hasSaved && !showPendingPreview) {
      this.dataset.hasSelection = 'false';
      this.#applyPickupPromptState(null);
      this.#setTriggerLabel(this.#headerDefaultLabel());
      this.#setCurrentSelectionHidden(true);
      this.#applySearchStageVisibility(false);
      this.#syncTriggerTooltipVisibility(false);
      this.#syncCartSelectionSummary(null);
      this.#refreshResultsCurrentBadge();
      return;
    }

    this.dataset.hasSelection = hasSaved ? 'true' : 'false';
    this.#applyPickupPromptState(hasSaved ? selection : null);
    this.#setTriggerLabel(
      hasSaved ? this.#composeTriggerLabel(selection) : this.#headerDefaultLabel()
    );
    if (showPendingPreview) {
      this.#renderDrawerCurrentSelectionFromPending();
      this.#setCurrentSelectionHidden(false);
    } else {
      this.#renderCurrentSelection(selection);
      this.#setCurrentSelectionHidden(this.isChangingBakery);
    }
    this.#applySearchStageVisibility(hasSaved || showPendingPreview);
    this.#syncTriggerTooltipVisibility(hasSaved);
    this.#syncCartSelectionSummary(hasSaved ? selection : null);
    this.#refreshResultsCurrentBadge();
  }

  /** In-drawer preview only — cleared on dialog close (not written to LS). */
  #shouldShowPendingCurrentSelectionPreview() {
    return (
      Boolean(this.pendingSelectionId && this.pendingSelection) &&
      Boolean(this.refs.dialog?.open) &&
      !this.isChangingBakery
    );
  }

  #buildPendingSelectionPreview() {
    const store = this.pendingSelection;
    if (!store) return null;
    const built = this.#buildSelectionDetails(store);
    return {
      storeId: store.id,
      slug: store.slug || LocationsService.extractShortId(store.id),
      name: store.name,
      brand: this.dataset.brand,
      countryCode: this.dataset.countryCode,
      pickupDay: this.pendingPickupDay || '',
      pickupSlot: this.pendingPickupSlot || '',
      distanceKm: built && Number.isFinite(built.distanceKm) ? built.distanceKm : null,
      address:
        built && built.address && typeof built.address === 'object' ? built.address : null,
      coordinates:
        built && built.coordinates && typeof built.coordinates === 'object'
          ? built.coordinates
          : null,
    };
  }

  #renderDrawerCurrentSelectionFromPending() {
    const preview = this.#buildPendingSelectionPreview();
    if (!preview) return;
    this.#renderCurrentSelection(preview);
  }

  #headerDefaultLabel() {
    return this.copy.headerDefault || '';
  }

  /**
   * Broadcast whether the saved selection still owes us a collection time.
   *
   * The header trigger lives in the header section, outside this component's subtree, and the cart
   * gate can render before the drawer upgrades — so the flag goes on `<html>` where any surface can
   * style off it without a per-surface JS contract. This component is the single writer; consumers
   * that need to *act* on the state (rather than style it) call `getPickupPromptState` themselves so
   * they never depend on the drawer having run.
   *
   * @param {object | null} selection saved selection, or null when none applies
   */
  #applyPickupPromptState(selection) {
    const state = selection ? getPickupPromptState(selection) : PICKUP_PROMPT_NONE;
    this.pickupPromptState = state;
    const incomplete = state !== PICKUP_PROMPT_NONE && state !== PICKUP_PROMPT_OK;
    this.dataset.pickupIncomplete = incomplete ? 'true' : 'false';
    document.documentElement.dataset.ambPickupIncomplete = incomplete ? 'true' : 'false';
  }

  /**
   * Build the header trigger label as `{bakery name} · {Today|Tomorrow}` so the user can see at a
   * glance which pickup day their saved selection applies to. Falls back to bakery name alone (or
   * the default label) when the day is missing/unknown.
   *
   * When the selection has no usable collection time the day is replaced by a prompt, so the pill
   * reads as unfinished instead of quietly showing a bakery whose pickup was never chosen (or has
   * since elapsed) — the shopper has no other signal that anything is outstanding.
   *
   * @param {object} selection
   * @returns {string}
   */
  #composeTriggerLabel(selection) {
    const name = String(selection?.name || '').trim() || this.#headerDefaultLabel();
    if (!name) return '';

    if (this.dataset.pickupIncomplete === 'true') {
      const prompt = this.copy.pickupSelectTime;
      return prompt ? `${name} · ${prompt}` : name;
    }

    const dayKey = String(selection?.pickupDay || '').toLowerCase();
    const dayLabel =
      dayKey === 'tomorrow'
        ? this.copy.pickupTomorrow
        : dayKey === 'today'
          ? this.copy.pickupToday
          : '';
    return dayLabel ? `${name} · ${dayLabel}` : name;
  }

  #setTriggerLabel(text) {
    const resolvedLabel = String(text || '').trim() || this.#headerDefaultLabel();
    this.#setLabelText(this.refs.triggerLabel, resolvedLabel);
    this.#syncExternalTriggerLabels(resolvedLabel);
  }

  #setLabelText(node, text) {
    if (!(node instanceof HTMLElement)) return;
    node.textContent = text;
    node.dataset.ssdLabelLoading = 'false';
    node.classList.remove('store-selector-drawer__trigger-label--loading');
  }

  #handleSsdHeaderTooltipCloseDocument = (event) => {
    if (document.getElementById('store-selector-drawer-root') !== this) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const closeButton = target.closest('[data-store-selector-tooltip-close]');
    if (!closeButton || !closeButton.closest('[data-ssd-header-trigger-tooltip]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#setTriggerTooltipDismissed();
  };

  #applySearchStageVisibility(hasSelection) {
    const shouldShowSearch = !hasSelection || this.isChangingBakery;
    this.refs.searchSection.classList.toggle('hidden', !shouldShowSearch);
    this.refs.searchChangeBakeryButton.classList.toggle('hidden', !hasSelection || this.isChangingBakery);
  }

  #setCurrentSelectionHidden(isHidden) {
    const hidden = Boolean(isHidden);
    this.refs.currentSelection.hidden = hidden;
    this.refs.currentSelection.classList.toggle('hidden', hidden);
  }

  #syncCartSelectionSummary(selection) {
    const summaries = document.querySelectorAll('[data-store-selection-cart-summary]');
    for (const summary of summaries) {
      if (!(summary instanceof HTMLElement)) continue;
      const storeName = summary.querySelector('[data-store-selection-name]');
      const pickupDay = summary.querySelector('[data-store-selection-pickup-day]');
      const pickupSlot = summary.querySelector('[data-store-selection-pickup-slot]');
      const hasSelection = Boolean(selection && selection.name);

      summary.classList.toggle('hidden', !hasSelection);
      if (!hasSelection) continue;

      if (storeName) storeName.textContent = selection.name || '';
      if (pickupDay) {
        const day = String(selection.pickupDay || '');
        pickupDay.textContent = day === 'tomorrow' ? 'Tomorrow' : day ? 'Today' : '';
      }
      if (pickupSlot) pickupSlot.textContent = selection.pickupSlot || '';
    }
  }

  #syncTriggerTooltipVisibility(hasSelection) {
    const shouldHide = Boolean(hasSelection) || this.#isTriggerTooltipDismissed();
    for (const tip of this.#getHeaderTriggerTooltipNodes()) {
      tip.classList.toggle('hidden', shouldHide);
    }
  }

  #getHeaderTriggerTooltipNodes() {
    return document.querySelectorAll('[data-ssd-header-trigger-tooltip]');
  }

  #isTriggerTooltipDismissed() {
    try {
      return window.localStorage.getItem(this.#tooltipDismissStorageKey()) === '1';
    } catch {
      return false;
    }
  }

  #setTriggerTooltipDismissed() {
    try {
      window.localStorage.setItem(this.#tooltipDismissStorageKey(), '1');
    } catch {
      /* no-op */
    }
    for (const tip of this.#getHeaderTriggerTooltipNodes()) {
      tip.classList.add('hidden');
    }
  }

  #tooltipDismissStorageKey() {
    const brand = String(this.dataset.brand || '').toLowerCase() || 'bakers';
    const country = String(this.dataset.countryCode || '').toUpperCase() || 'AU';
    return `amb_store_selector_tooltip_dismissed_${brand}_${country}`;
  }

  #hasCurrentMyBakery(selection) {
    if (!selection || typeof selection !== 'object') return false;
    const storeId = String(selection.storeId || '').trim();
    const name = String(selection.name || '').trim();
    const selectionBrand = String(selection.brand || '').trim().toLowerCase();
    const selectionCountry = String(selection.countryCode || '').trim().toUpperCase();
    const currentBrand = String(this.dataset.brand || '').trim().toLowerCase();
    const currentCountry = String(this.dataset.countryCode || '').trim().toUpperCase();
    if (!storeId || !name) return false;
    if (!selectionBrand || !selectionCountry) return false;
    return selectionBrand === currentBrand && selectionCountry === currentCountry;
  }

  #renderCurrentSelection(selection) {
    if (!(this.refs.currentSelectionCard instanceof HTMLElement)) return;
    this.refs.currentSelectionCard.innerHTML = '';
    const fragment = this.refs.entryTemplate.content.cloneNode(true);
    const article = fragment.querySelector('[data-store-entry]');
    if (!(article instanceof HTMLElement)) return;
    const legacyDetails = selection.details && typeof selection.details === 'object' ? selection.details : null;
    const foundStore = this.#findLocationByStoreId(selection.storeId);
    const flatDistKm = Number(selection.distanceKm);
    const store =
      foundStore ||
      legacyDetails || {
        id: selection.storeId,
        slug: selection.slug,
        name: selection.name,
        status: { isOpen: false, isTemporarilyClosed: false },
        address:
          selection.address && typeof selection.address === 'object'
            ? selection.address
            : {},
        coordinates:
          selection.coordinates && typeof selection.coordinates === 'object'
            ? selection.coordinates
            : null,
        distanceKm: Number.isFinite(flatDistKm) ? flatDistKm : null,
      };
    if (foundStore && Number.isFinite(flatDistKm) && !Number.isFinite(foundStore.distanceKm)) {
      store.distanceKm = flatDistKm;
    }
    this.#populateEntry(article, store);
    article.classList.add('store-selector-entry--current');
    article.removeAttribute('tabindex');
    article.setAttribute('role', 'presentation');
    article.setAttribute('aria-selected', 'true');
    let currentLabel = article.querySelector('.store-selector-drawer__current-label');
    if (!(currentLabel instanceof HTMLElement)) {
      currentLabel = document.createElement('p');
      currentLabel.className = 'store-selector-drawer__current-label';
      article.prepend(currentLabel);
    } else {
      currentLabel.classList.remove('hidden');
    }
    currentLabel.textContent = this.copy.currentLabel;
    const selectButton = article.querySelector('[data-store-select]');
    if (selectButton instanceof HTMLButtonElement) {
      selectButton.hidden = true;
    }
    this.refs.currentSelectionCard.appendChild(fragment);
  }

  async #ensureLocationsLoaded() {
    if (this.loaded) return;

    this.locationsService = new LocationsService({
      shopDomain: this.dataset.shopDomain,
      apiVersion: this.dataset.storefrontApiVersion,
      accessToken: this.dataset.storefrontAccessToken,
      defaultTimezone: this.dataset.defaultTimezone,
    });

    if (cachedAllLocations) {
      this.allLocations = cachedAllLocations;
      this.#onLocationsReady();
      this.loaded = true;
      return;
    }

    if (!cachedLocationsPromise) {
      cachedLocationsPromise = this.locationsService.getAllLocations();
    }

    try {
      cachedAllLocations = await cachedLocationsPromise;
      this.allLocations = cachedAllLocations;
      this.#onLocationsReady();
    } catch (_) {
      cachedLocationsPromise = null;
      this.allLocations = [];
      this.#setResultsHidden(true);
      this.refs.resultsContainer.innerHTML = '';
      this.#setSearchFeedback(this.copy.emptyNoResultsText);
    } finally {
      this.loaded = true;
    }
  }

  #onLocationsReady() {
    if (this.pendingSelectionId && !this.pendingSelection) {
      this.pendingSelection = this.#findLocationByStoreId(this.pendingSelectionId);
      this.#syncPickupPanel();
      this.#updateSaveState();
    }
    this.#syncSelectionDisplay();
  }

  #hydratePendingFromSavedSelection() {
    const stateApi = window.StoreSelectionState;
    const selection = stateApi && typeof stateApi.getSelection === 'function' ? stateApi.getSelection() : null;
    this.pendingSelectionId = selection?.storeId || '';
    this.pendingSelection = this.#findLocationByStoreId(this.pendingSelectionId);
    this.pendingPickupDay = selection?.pickupDay || '';
    this.pendingPickupSlot = selection?.pickupSlot || '';
    this.#syncPickupPanel();
    this.#updateSaveState();
  }

  #handleSearchInput = () => {
    if (!(this.refs.searchInput instanceof HTMLInputElement)) return;
    if (this.searchDebounce) window.clearTimeout(this.searchDebounce);

    // Postcode-shaped queries hit a paid geocoding API, so debounce them a
    // little longer than instant text matching to cut request volume.
    const delay = this.#looksLikePostcode(this.refs.searchInput.value.trim()) ? 500 : 250;
    this.searchDebounce = window.setTimeout(async () => {
      await this.#runSearch(this.refs.searchInput.value);
    }, delay);
  };

  /**
   * @param {string} rawQuery
   */
  async #runSearch(rawQuery) {
    const generation = (this.searchGeneration = (this.searchGeneration || 0) + 1);
    const current = () => this.searchGeneration === generation;
    await this.#ensureLocationsLoaded();
    if (!current()) return;
    this.#clearSearchMessage();
    const query = String(rawQuery || '').trim().toLowerCase();

    // Typing already painted a list for this exact text. Enter and a late
    // geocode must not replace it — "merrick" stays Merrick, not Merrick plus
    // Oceanside from the nearest-3 place frame, and not the no-pickup message.
    if (this.#fieldHasKeptResults(query)) return;

    if (!query) {
      this.searchOrigin = null;
      this.lastRenderedQuery = '';
      this.#setResultsHidden(true);
      this.refs.resultsContainer.innerHTML = '';
      this.#resetSearchFeedback();
      return;
    }

    // A postcode is a place, same as the store locator: geocode it and list the
    // bakeries in that frame. A name match still wins when geocoding is down.
    const isPostcode = this.#looksLikePostcode(query);
    if (isPostcode) {
      const outcome = await this.#renderGeocodedResults(query, generation);
      if (!current() || outcome === 'rendered' || outcome === 'aborted') return;
      if (outcome === 'empty') {
        this.lastRenderedQuery = '';
        this.#showNoNearbyBakeriesMessage();
        return;
      }
    }

    // Store-locator store search: bakery name only. "merrick" is the Merrick
    // bakery, not every store inside that town (BDH-2729).
    this.searchOrigin = null;
    const named = this.#sortWithDistance(this.#locationsMatchingQuery(query));
    if (!current()) return;
    if (named.length) {
      this.lastRenderedQuery = query;
      this.#renderResults(named.slice(0, 40));
      this.#setSearchFeedback(this.#formatResultsCount(named.length));
      return;
    }

    // Store-locator place search. "new york" matches no bakery name, so it is
    // geocoded and framed the same way the finder frames a chosen place.
    if (!isPostcode && query.length >= MIN_FALLBACK_GEOCODE_LENGTH) {
      const outcome = await this.#renderGeocodedResults(query, generation);
      if (!current() || outcome === 'rendered' || outcome === 'aborted') return;
    }

    if (!current()) return;
    this.lastRenderedQuery = '';
    this.#showNoNearbyBakeriesMessage();
  }

  /**
   * Geocode `query` and list the Click & Collect bakeries in the same frame the
   * store locator would fit: the place bounds, expanded to the nearest bakeries.
   * @param {string} query
   * @param {number} generation - `#runSearch` token; a newer keystroke wins
   * @returns {Promise<'rendered'|'empty'|'unavailable'|'aborted'>} 'unavailable'
   *   means the query could not be geocoded (no map token, no match, or a
   *   provider error), leaving the caller to decide the fallback.
   */
  async #renderGeocodedResults(query, generation) {
    this.#setSearchFeedback(this.copy.searching);

    let origin = null;
    try {
      origin = await this.#geocodePostcode(query);
    } catch (error) {
      // Superseded by a newer keystroke — the newer run owns the UI.
      if (error?.name === 'AbortError') return 'aborted';
      origin = null;
    }
    if (this.searchGeneration !== generation) return 'aborted';
    if (!origin) return 'unavailable';

    this.searchOrigin = origin;
    const framed = this.#bakeriesInSearchFrame(origin);
    if (!framed.length) return 'empty';
    if (this.searchGeneration !== generation) return 'aborted';

    this.lastRenderedQuery = query;
    this.#renderResults(framed.slice(0, 40));
    this.#setSearchFeedback(this.#formatResultsCount(framed.length));
    return 'rendered';
  }

  /**
   * Bakeries a store-locator place selection would show.
   *
   * When the geocoder names a city or province that bakeries actually use
   * ("New York"), those bakeries are the result — every New York bakery, and
   * not a neighbouring state that happens to sit inside an expanded box
   * (BDH-2729). A postcode or suburb with no such name falls through to the
   * locator frame: geocode bounds expanded to the nearest bakeries
   * (`zoomToSearchedLocation` / BDH-2442).
   * @param {{ lat: number, lng: number, bbox?: number[]|null, primaryName?: string }} origin
   * @returns {object[]}
   */
  #bakeriesInSearchFrame(origin) {
    const positioned = [];
    for (const location of this.#selectableLocations()) {
      const lat = Number(location.coordinates?.latitude);
      const lng = Number(location.coordinates?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      positioned.push({
        location,
        lat,
        lng,
        distanceKm: this.#calculateDistance(origin.lat, origin.lng, lat, lng),
      });
    }
    positioned.sort((a, b) => a.distanceKm - b.distanceKm);

    const withDistance = (point) => ({ ...point.location, distanceKm: point.distanceKm });
    const placeName = String(origin.primaryName || '').trim().toLowerCase();
    if (placeName) {
      const namedPlace = positioned.filter((point) => {
        const city = String(point.location.address?.city || '').trim().toLowerCase();
        const province = String(point.location.address?.province || '').trim().toLowerCase();
        const provinceCode = String(point.location.address?.provinceCode || '').trim().toLowerCase();
        return city === placeName || province === placeName || provinceCode === placeName;
      });
      if (namedPlace.length) return namedPlace.map(withDistance);
    }

    let minLat = origin.lat;
    let maxLat = origin.lat;
    let minLng = origin.lng;
    let maxLng = origin.lng;
    if (Array.isArray(origin.bbox) && origin.bbox.length === 4) {
      const [west, south, east, north] = origin.bbox.map(Number);
      if ([west, south, east, north].every(Number.isFinite)) {
        minLng = Math.min(minLng, west);
        maxLng = Math.max(maxLng, east);
        minLat = Math.min(minLat, south);
        maxLat = Math.max(maxLat, north);
      }
    }
    for (const point of positioned.slice(0, NEAREST_BAKERIES_IN_FRAME)) {
      minLat = Math.min(minLat, point.lat);
      maxLat = Math.max(maxLat, point.lat);
      minLng = Math.min(minLng, point.lng);
      maxLng = Math.max(maxLng, point.lng);
    }

    return positioned
      .filter(
        (point) =>
          point.lat >= minLat && point.lat <= maxLat && point.lng >= minLng && point.lng <= maxLng
      )
      .map(withDistance);
  }

  /**
   * Terminal zero-result state for a search. Every path that ends with an empty
   * list must land here: `#setSearchFeedback` is a no-op, so returning without
   * this leaves the shopper staring at an empty drawer with no explanation.
   */
  #showNoNearbyBakeriesMessage() {
    this.#setResultsHidden(true);
    this.refs.resultsContainer.innerHTML = '';
    this.#clearPendingPickupForm();
    // The sign-up prompt is guest-only — an already signed-in shopper being told
    // to join the loyalty programme reads as a bug. Members keep the "list every
    // Click & Collect bakery" fallback instead, so neither audience is left with
    // a heading and no action beneath it.
    const isGuest = this.dataset.customerLoggedIn !== 'true';
    const signup = isGuest
      ? {
          label: this.copy.noResultsWithinRadiusSignup,
          url: this.dataset.createAccountUrl || '/pages/create-account',
          suffix: this.copy.noResultsWithinRadiusSignupSuffix,
        }
      : null;
    this.#showSearchMessage(this.copy.noResultsWithinRadius, signup, { showJoin: isGuest, showAll: !isGuest });
  }

  /**
   * Drop the in-drawer bakery + pickup selection so the "When" panel can't sit
   * open underneath a zero-result search. The panel is keyed off the pending
   * bakery, so a shopper who picked one and then searched again for a place with
   * no bakeries was left choosing a collection slot for a bakery that is no
   * longer on screen anywhere — with Save enabled for it.
   *
   * Only pending (in-drawer) state is touched: a saved "My bakery" lives in
   * localStorage and is untouched, and `#syncSelectionDisplay` puts its card
   * back in place of the pending preview.
   */
  #clearPendingPickupForm() {
    const hasPendingState = Boolean(
      this.pendingSelectionId || this.pendingSelection || this.pendingPickupDay || this.pendingPickupSlot
    );
    if (!hasPendingState) return;

    this.pendingSelectionId = '';
    this.pendingSelection = null;
    this.pendingPickupDay = '';
    this.pendingPickupSlot = '';
    // Resets the select back to its placeholder option — #syncPickupPanel skips
    // slot rendering once there is no pending bakery.
    this.#renderPickupSlots();
    this.#syncPickupPanel();
    this.#updateSaveState();
    this.#syncSelectionDisplay();
  }

  async useCurrentLocation(event) {
    event.preventDefault();
    await this.#ensureLocationsLoaded();
    this.#clearSearchMessage();

    if (!navigator.geolocation) {
      this.#setSearchFeedback(this.copy.geolocationNotSupported);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });
      });

      this.userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
      // The shopper explicitly asked for their GPS location, so drop any
      // postcode-derived origin that would otherwise win in #sortWithDistance.
      this.searchOrigin = null;
      const query = this.refs.searchInput.value.trim().toLowerCase();
      const selectable = this.#selectableLocations();
      const source = query ? this.#locationsMatchingQuery(query, selectable) : selectable;
      const sorted = this.#sortWithDistance(source);

      if (!sorted.length) {
        this.#setResultsHidden(true);
        this.refs.resultsContainer.innerHTML = '';
        this.#setSearchFeedback(this.copy.emptyNoResultsText);
        return;
      }

      this.#renderResults(sorted.slice(0, 40));
      this.#setSearchFeedback(this.#formatResultsCount(sorted.length));
    } catch (_) {
      this.#setSearchFeedback(this.copy.geolocationError);
    }
  }

  /**
   * Locations the shopper may pick in the SSD — bakeries with Click & Collect
   * enabled (`custom.mf37_bakery_clickcollect_status` = true), minus whichever
   * bakery the "My bakery" card is currently showing.
   * `this.allLocations` deliberately keeps the FULL catalog so
   * `#findLocationByStoreId` can still resolve an already-saved selection
   * (current-selection card, MF10 hydration, internalBakeryId lookup) even if
   * that bakery has since had Click & Collect turned off.
   */
  #selectableLocations() {
    // The bakery already shown in the "My bakery" card above the list is
    // excluded so it can't appear twice in the drawer. Gated on that card being
    // VISIBLE, which is what makes the "Change bakery" flow still work: that
    // flow hides the card, so nothing is excluded and the shopper can find and
    // re-pick their own bakery, badged as current via `#applyCurrentBadge`.
    const shownStoreId = this.#currentSelectionCardStoreId();
    return this.allLocations.filter((location) => {
      if (!LocationsService.isClickCollectEnabled(location)) return false;
      if (shownStoreId && this.#isSameStoreId(location.id, shownStoreId)) return false;
      return true;
    });
  }

  /**
   * Bakery-name matches, the same filter the store locator uses for its store
   * suggestions (`LocationsService.searchLocationsByName`). City, province, and
   * street stay out of this pass so "merrick" does not list Oceanside.
   * @param {string} query - already trimmed and lowercased
   * @param {object[]} [locations]
   * @returns {object[]}
   */
  #locationsMatchingQuery(query, locations = this.#selectableLocations()) {
    return locations.filter((location) => String(location.name || '').toLowerCase().includes(query));
  }

  /**
   * The result list already on screen was rendered for `query`.
   * @param {string} [query] normalized query; defaults to the field value
   * @returns {boolean}
   */
  #fieldHasKeptResults(query = this.#normalizedFieldQuery()) {
    if (!query || query !== this.lastRenderedQuery) return false;
    return this.#visibleResultCount() > 0;
  }

  /**
   * @returns {string}
   */
  #normalizedFieldQuery() {
    if (!(this.refs.searchInput instanceof HTMLInputElement)) return '';
    return this.refs.searchInput.value.trim().toLowerCase();
  }

  /**
   * Cards in the visible results list. Falls back to the ref attribute when the
   * component ref is not a plain element, so Enter can still see the list.
   * @returns {number}
   */
  #visibleResultCount() {
    const fromRef = this.refs.resultsContainer;
    const el =
      fromRef instanceof HTMLElement ? fromRef : this.querySelector('[ref="resultsContainer"]');
    if (!(el instanceof HTMLElement) || el.classList.contains('hidden')) return 0;
    return el.querySelectorAll('[data-store-entry]').length;
  }

  /**
   * Drop a pending keystroke search and any geocode still in flight, and retire
   * the current `#runSearch` generation so it cannot paint after we decide to
   * keep the list.
   */
  #keepShownResults() {
    if (this.searchDebounce) {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
    if (this.geoAbortController) {
      this.geoAbortController.abort();
      this.geoAbortController = null;
    }
    this.searchGeneration = (this.searchGeneration || 0) + 1;
  }

  /**
   * Maps the drawer's country code (AU | CA | US) to the lowercase ISO code
   * the geocoding APIs expect for country restriction.
   * @returns {string}
   */
  #countryParam() {
    return String(this.dataset.countryCode || '').trim().toLowerCase();
  }

  /**
   * Heuristic: does the query look like a postcode/zip for the active market?
   * AU = 4 digits, US = 5 digits (optional +4), CA = full "A1A 1A1" (space
   * optional) or a 3-char FSA. Falls back to a permissive numeric/CA test when
   * the country is unknown so a shopper still gets the geocode path.
   * @param {string} query - trimmed, lower- or original-cased query
   * @returns {boolean}
   */
  #looksLikePostcode(query) {
    const value = String(query || '').trim();
    if (!value) return false;
    const country = this.#countryParam();
    const au = /^\d{4}$/;
    const us = /^\d{5}(-\d{4})?$/;
    const caFull = /^[a-z]\d[a-z]\s?\d[a-z]\d$/i;
    const caFsa = /^[a-z]\d[a-z]$/i;

    if (country === 'au') return au.test(value);
    if (country === 'us') return us.test(value);
    if (country === 'ca') return caFull.test(value) || caFsa.test(value);
    // Unknown country: accept any of the known shapes.
    return au.test(value) || us.test(value) || caFull.test(value) || caFsa.test(value);
  }

  /**
   * Geocode a place the same way the store locator does. Mapbox uses Geocoding
   * v5 (`map-provider-mapbox.js` `geocode` / `searchPlaces`) — the storefront
   * token is not allowed to call Search API v6, so that endpoint 403s and Enter
   * replaced a suggestion list with the empty state (BDH-2729). `feature.text`
   * is the short place name ("New York"), which is what city/province matching
   * compares. Returns null on missing token / no result / error, letting the
   * caller fall back to the text match.
   * @param {string} query
   * @returns {Promise<{lat: number, lng: number, bbox: number[]|null, primaryName: string} | null>}
   */
  async #geocodePostcode(query) {
    const settings = window.Shopify?.theme?.settings;
    const apiKey = settings ? settings.map_access_token : undefined;
    if (!apiKey) {
      console.warn('StoreSelectorDrawer: map_access_token not configured; postcode geocoding disabled.');
      return null;
    }

    // Cancel any geocode still in flight from a previous keystroke.
    if (this.geoAbortController) this.geoAbortController.abort();
    this.geoAbortController = new AbortController();
    const signal = this.geoAbortController.signal;

    const provider = (settings.map_provider || 'google').toLowerCase();
    const country = this.#countryParam();

    try {
      if (provider === 'mapbox') {
        const url = new URL(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
        );
        url.searchParams.set('access_token', apiKey);
        if (country) url.searchParams.set('country', country);
        url.searchParams.set('types', 'region,postcode,place,locality');
        url.searchParams.set('limit', '1');
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Mapbox geocoding failed: ${response.status}`);
        const data = await response.json();
        const feature = data.features?.[0];
        if (!feature || !Array.isArray(feature.center)) return null;
        const [lng, lat] = feature.center;
        return {
          lat,
          lng,
          bbox: Array.isArray(feature.bbox) ? feature.bbox : null,
          primaryName: feature.text || '',
        };
      }

      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.set('address', query);
      if (country) url.searchParams.set('components', `country:${country}`);
      url.searchParams.set('key', apiKey);
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error(`Google geocoding failed: ${response.status}`);
      const data = await response.json();
      const result = data.results?.[0];
      const location = result?.geometry?.location;
      if (data.status !== 'OK' || !location) return null;
      const viewport = result.geometry.viewport;
      const bbox = viewport
        ? [viewport.southwest.lng, viewport.southwest.lat, viewport.northeast.lng, viewport.northeast.lat]
        : null;
      return {
        lat: location.lat,
        lng: location.lng,
        bbox,
        primaryName: result.address_components?.[0]?.long_name || '',
      };
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      console.warn('StoreSelectorDrawer: postcode geocoding error', error);
      return null;
    }
  }

  #sortWithDistance(locations) {
    // A postcode/zip geocode sets `searchOrigin`; "use my location" sets
    // `userLocation`. Either provides the point to sort distances from.
    const origin = this.searchOrigin || this.userLocation;
    if (!origin) return [...locations];

    const withDistance = [];
    const withoutDistance = [];

    for (const location of locations) {
      const lat = Number(location.coordinates?.latitude);
      const lng = Number(location.coordinates?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        withoutDistance.push(location);
        continue;
      }

      withDistance.push({
        ...location,
        distanceKm: this.#calculateDistance(origin.lat, origin.lng, lat, lng),
      });
    }

    withDistance.sort((a, b) => a.distanceKm - b.distanceKm);
    return [...withDistance, ...withoutDistance];
  }

  #calculateDistance(lat1, lon1, lat2, lon2) {
    if (![lat1, lon1, lat2, lon2].every((value) => Number.isFinite(value))) return Infinity;
    const radius = 6371;
    const dLat = this.#toRadians(lat2 - lat1);
    const dLon = this.#toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.#toRadians(lat1)) * Math.cos(this.#toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return radius * c;
  }

  #toRadians(value) {
    if (!Number.isFinite(value)) return 0;
    return value * (Math.PI / 180);
  }

  #formatDistance(distanceKm) {
    if (!Number.isFinite(distanceKm)) return '';
    if (this.dataset.countryCode === 'US') {
      const miles = distanceKm * 0.621371;
      return miles < 1 ? `${Math.round(miles * 5280)} ft` : `${miles.toFixed(2)} miles`;
    }
    return distanceKm < 1 ? `${Math.round(distanceKm * 1000)} m` : `${distanceKm.toFixed(2)} km`;
  }

  #formatResultsCount(count) {
    const number = Number.isFinite(count) ? count : 0;
    return `${number} ${number === 1 ? 'result' : 'results'}`;
  }

  #renderResults(locations) {
    this.refs.resultsContainer.innerHTML = '';
    this.renderedLocations = locations;

    // Batched into one insertion because `showAllBakeries` renders the entire
    // Click & Collect catalog, which is far longer than the 40-result cap the
    // search paths use.
    const batch = document.createDocumentFragment();
    for (const store of locations) {
      const fragment = this.refs.entryTemplate.content.cloneNode(true);
      const article = fragment.querySelector('[data-store-entry]');
      if (!(article instanceof HTMLElement)) continue;
      this.#populateEntry(article, store);
      batch.appendChild(fragment);
    }
    this.refs.resultsContainer.appendChild(batch);

    this.#setResultsHidden(false);
    this.#initResultsRovingFocus();
    this.#syncPickupPanel();
    this.#updateSaveState();
  }

  #populateEntry(article, store) {
    article.dataset.storeId = store.id;
    article.setAttribute('aria-selected', String(this.pendingSelectionId === store.id));
    article.classList.toggle('store-selector-entry--pending', this.pendingSelectionId === store.id);

    const nameEl = article.querySelector('[data-store-name]');
    if (nameEl) nameEl.textContent = store.name || '';

    const status = this.#pickupAlignedStoreStatus(store);
    const statusEl = article.querySelector('[data-store-status]');
    if (statusEl) {
      let statusText = this.copy.statusClosed;
      if (status.isTemporarilyClosed) statusText = this.copy.statusTemporarilyClosed;
      else if (status.isOpen) statusText = this.copy.statusOpen;
      statusEl.textContent = statusText;
      statusEl.className = status.isOpen ? 'store-badge store-badge--open' : 'store-badge store-badge--closed';
    }

    const nextStatusEl = article.querySelector('[data-store-next-status]');
    if (nextStatusEl) {
      const nextText = this.#formatNextStateChange(status.nextStateChangeData);
      nextStatusEl.textContent = nextText;
      nextStatusEl.hidden = !nextText;
    }

    const closureReasonEl = article.querySelector('[data-store-closure-reason]');
    if (closureReasonEl) {
      const reason = status.isTemporarilyClosed && status.statusText && status.statusText !== 'Temporarily Closed'
        ? status.statusText
        : '';
      closureReasonEl.textContent = reason;
      closureReasonEl.hidden = !reason;
    }

    const address = store.address || {};
    const addressEl = article.querySelector('[data-store-address]');
    const addressParts = [address.address1, address.address2, address.city, address.province, address.zip].filter(Boolean);
    if (addressEl instanceof HTMLAnchorElement && addressParts.length > 0) {
      const mapsQuery = [address.address1, address.city, address.province, address.zip].filter(Boolean).join(' ');
      addressEl.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`;
      const addressText = addressEl.querySelector('[data-store-address-text]');
      if (addressText) {
        const cityLine = address.city || address.province || address.zip
          ? address.province
            ? `${address.city || ''}, ${address.provinceCode || address.province} ${address.zip || ''}`.trim()
            : `${address.city || ''} ${address.zip || ''}`.trim()
          : '';
        addressText.textContent = [address.address1, address.address2, cityLine].filter(Boolean).join(', ');
      }
      addressEl.hidden = false;
    }

    const slug = store.slug || LocationsService.extractShortId(store.id);
    const detailEl = article.querySelector('[data-store-detail-url]');
    if (detailEl instanceof HTMLAnchorElement) {
      detailEl.href = `/pages/bakeries/${slug}`;
    }

    const distanceEl = article.querySelector('[data-store-distance]');
    if (distanceEl) {
      const hasDistance = Number.isFinite(store.distanceKm) && store.distanceKm > 0.05;
      distanceEl.textContent = hasDistance ? this.#formatDistance(store.distanceKm) : '';
      distanceEl.hidden = !hasDistance;
    }

    const selectButton = article.querySelector('[data-store-select]');
    if (selectButton instanceof HTMLButtonElement) {
      selectButton.dataset.storeId = store.id;
      selectButton.setAttribute('aria-pressed', String(this.pendingSelectionId === store.id));
      const selectLabel = this.copy.selectAsMyBakery || 'Select as my bakery';
      selectButton.setAttribute(
        'aria-label',
        `${selectLabel}: ${store.name || 'bakery'}`
      );
    }

    this.#applyCurrentBadge(article, store.id);
  }

  #applyCurrentBadge(article, storeId) {
    const badge = article.querySelector('.store-selector-drawer__current-label');
    if (!(badge instanceof HTMLElement)) return;
    const isCurrent = this.#isCurrentSelectionStore(storeId);
    badge.classList.toggle('hidden', !isCurrent);
    article.classList.toggle('store-selector-entry--is-current', isCurrent);
    const selectButton = article.querySelector('[data-store-select]');
    if (selectButton instanceof HTMLElement) {
      selectButton.hidden = isCurrent;
    }
  }

  /**
   * Canonical comparison for two store identities. Row shapes are NOT
   * consistent across the sources that feed this drawer — localStorage, the MF10
   * customer metafield, the external `data-pending-store-id` handoff, and the
   * Storefront catalog can each hold a full GID, a bare numeric id, or a slug
   * for the same bakery. `#findLocationByStoreId` has always tolerated that;
   * anything comparing ids MUST use this helper rather than `===`, or the same
   * bakery reads as two different ones.
   * @param {unknown} a
   * @param {unknown} b
   */
  #isSameStoreId(a, b) {
    const rawA = String(a || '').trim();
    const rawB = String(b || '').trim();
    if (!rawA || !rawB) return false;
    if (rawA === rawB) return true;
    const shortA = LocationsService.extractShortId(rawA);
    const shortB = LocationsService.extractShortId(rawB);
    return Boolean(shortA && shortB && shortA === shortB);
  }

  /**
   * The bakery currently shown in the "My bakery" card, or '' when that card is
   * hidden. This is the PENDING bakery while the in-drawer preview is up and the
   * saved one otherwise — the preview renders before anything is written to
   * localStorage, so reading only the saved selection misses it.
   * @returns {string}
   */
  #currentSelectionCardStoreId() {
    if (this.refs.currentSelection?.hidden) return '';
    if (this.#shouldShowPendingCurrentSelectionPreview()) {
      return String(this.pendingSelectionId || '').trim();
    }
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.getSelection !== 'function') return '';
    const selection = stateApi.getSelection();
    if (!this.#hasCurrentMyBakery(selection)) return '';
    return String(selection.storeId || '').trim();
  }

  #isCurrentSelectionStore(storeId) {
    if (!storeId) return false;
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.getSelection !== 'function') return false;
    const selection = stateApi.getSelection();
    if (!this.#hasCurrentMyBakery(selection)) return false;
    return this.#isSameStoreId(selection.storeId, storeId);
  }

  #refreshResultsCurrentBadge() {
    const cards = this.refs.resultsContainer?.querySelectorAll('[data-store-entry]');
    if (!cards) return;
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      this.#applyCurrentBadge(card, card.dataset.storeId);
    }
  }

  #formatNextStateChange(nextState) {
    if (!nextState?.key) return '';
    return this.translator.translate(nextState.key, nextState.args || {});
  }

  #handleResultClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const selectButton = target.closest('[data-store-select]');
    if (selectButton instanceof HTMLButtonElement) {
      this.#selectResultStore(selectButton.dataset.storeId);
      return;
    }
    // The card wraps two links — the address (maps, new tab) and "View details".
    // Each does its own job; neither also picks the bakery.
    if (target.closest('a')) return;
    const card = target.closest('[data-store-entry]');
    if (!(card instanceof HTMLElement)) return;
    this.#selectResultStore(card.dataset.storeId);
  };

  /**
   * Picking a bakery from the results list, whether by its "Select as my bakery"
   * CTA or by clicking the card body. Both must advance to the pickup day/slot
   * panel: selecting without advancing renders the chosen bakery in the "My
   * bakery" card while leaving it in the still-visible results list below, so it
   * reads as a duplicate and the shopper never reaches the time selector.
   * `#handleResultKeydown` activates options through here too.
   * @param {string | undefined} storeId
   */
  #selectResultStore(storeId) {
    if (!storeId) return;
    this.#setPendingSelection(storeId, {
      preservePickupSelection: storeId === this.pendingSelectionId,
    });
    this.#focusPendingPickupAfterStoreSelect();
  }

  /**
   * Returns the rendered bakery option cards (the `role="option"` articles) as
   * an array, in DOM order.
   * @returns {HTMLElement[]}
   */
  #getResultOptions() {
    if (!(this.refs.resultsContainer instanceof HTMLElement)) return [];
    return Array.from(this.refs.resultsContainer.querySelectorAll('[data-store-entry]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * Roving-tabindex helper: exactly one option (and its inner actions) is in the
   * tab sequence at a time, so Tab enters the listbox once and the Arrow keys
   * move within it — the standard ARIA listbox keyboard model. Non-active
   * options and their inner controls are removed from the tab order (-1).
   * @param {HTMLElement} option
   * @param {boolean} tabbable
   */
  #setOptionTabbable(option, tabbable) {
    if (!(option instanceof HTMLElement)) return;
    option.setAttribute('tabindex', tabbable ? '0' : '-1');
    for (const control of option.querySelectorAll('[data-store-detail-url], [data-store-select]')) {
      if (control instanceof HTMLElement) control.setAttribute('tabindex', tabbable ? '0' : '-1');
    }
  }

  /**
   * Makes a single option the active (tabbable) one and optionally moves focus
   * to it. Falls back to the first option when the requested one isn't in the
   * current result set.
   * @param {HTMLElement | null} option
   * @param {{ focus?: boolean }} [options]
   */
  #setActiveOption(option, { focus = false } = {}) {
    const options = this.#getResultOptions();
    if (!options.length) return;
    const target = option instanceof HTMLElement && options.includes(option) ? option : options[0];
    for (const opt of options) this.#setOptionTabbable(opt, opt === target);
    if (focus) target.focus();
  }

  /**
   * Initialises roving tabindex after the results render: the pending/selected
   * option is the entry point if present, otherwise the first option. Focus is
   * left on the search input so type-to-filter keeps working — the shopper
   * presses ArrowDown (or Tab) to move into the list.
   */
  #initResultsRovingFocus() {
    const options = this.#getResultOptions();
    if (!options.length) return;
    const preferred = options.find((opt) => opt.dataset.storeId === this.pendingSelectionId) || options[0];
    this.#setActiveOption(preferred, { focus: false });
  }

  /**
   * `<input type="search">` fires `search` on Enter. keydown already runs the
   * search, so ignore the duplicate from that same keypress.
   */
  #handleNativeSearch = () => {
    if (this.searchCommitLock) return;
    if (this.#fieldHasKeptResults()) {
      this.#keepShownResults();
      return;
    }
    this.#submitSearch();
  };

  async #submitSearch() {
    if (!(this.refs.searchInput instanceof HTMLInputElement)) return;
    const value = this.refs.searchInput.value.trim();
    if (!value) return;
    // The store finder has no Enter action once a suggestion is showing. The
    // drawer matches that: a list already rendered for this text stays put.
    if (this.#fieldHasKeptResults(value.toLowerCase())) {
      this.#keepShownResults();
      return;
    }
    if (this.searchDebounce) window.clearTimeout(this.searchDebounce);
    await this.#runSearch(value);
  }

  #handleSearchKeydown = (event) => {
    // ArrowDown moves focus into the bakery list. Enter keeps a list typing
    // already rendered; otherwise it runs the same search as typing.
    if (event.key === 'Enter') {
      // Stop the dialog from treating Enter as a click on the close button,
      // which is the first button in the drawer.
      event.preventDefault();
      event.stopPropagation();
      this.searchCommitLock = true;
      void this.#submitSearch().finally(() => {
        this.searchCommitLock = false;
      });
      return;
    }

    if (event.key !== 'ArrowDown') return;
    if (this.refs.resultsContainer?.classList.contains('hidden')) return;
    const options = this.#getResultOptions();
    if (!options.length) return;
    event.preventDefault();
    this.#setActiveOption(this.#getResultOptions().find((opt) => opt.getAttribute('tabindex') === '0') || options[0], {
      focus: true,
    });
  };

  #handleResultKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const option = target.closest('[data-store-entry]');
    if (!(option instanceof HTMLElement)) return;

    const options = this.#getResultOptions();
    const index = options.indexOf(option);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.#setActiveOption(options[Math.min(index + 1, options.length - 1)], { focus: true });
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        // From the first option, ArrowUp returns to the search input.
        if (index <= 0) {
          if (this.refs.searchInput instanceof HTMLElement) this.refs.searchInput.focus();
          return;
        }
        this.#setActiveOption(options[index - 1], { focus: true });
        return;
      }
      case 'Home': {
        event.preventDefault();
        this.#setActiveOption(options[0], { focus: true });
        return;
      }
      case 'End': {
        event.preventDefault();
        this.#setActiveOption(options[options.length - 1], { focus: true });
        return;
      }
      case 'Enter':
      case ' ': {
        // Only act when the option card itself is focused — let Enter/Space on
        // the inner "View details" link / "Select" button trigger their native
        // behaviour (handled via #handleResultClick).
        if (target !== option) return;
        event.preventDefault();
        this.#selectResultStore(option.dataset.storeId);
        return;
      }
    }
  };

  #setPendingSelection(storeId, options = {}) {
    const preservePickupSelection = Boolean(options.preservePickupSelection);
    const isSameStore = this.pendingSelectionId === storeId;
    this.pendingSelectionId = storeId;
    this.pendingSelection = this.#findLocationByStoreId(storeId);
    if (!(preservePickupSelection && isSameStore)) {
      this.pendingPickupDay = this.#pickInitialPickupDay(this.pendingSelection, this.pendingPickupDay);
      this.pendingPickupSlot = '';
    } else if (!this.pendingPickupDay) {
      this.pendingPickupDay = this.#pickInitialPickupDay(this.pendingSelection, '');
    }
    const cards = this.refs.resultsContainer.querySelectorAll('[data-store-entry]');

    let matchedCard = null;
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const active = card.dataset.storeId === storeId;
      if (active) matchedCard = card;
      card.classList.toggle('store-selector-entry--pending', active);
      card.setAttribute('aria-selected', String(active));
      const button = card.querySelector('[data-store-select]');
      if (button instanceof HTMLButtonElement) {
        button.setAttribute('aria-pressed', String(active));
      }
    }

    // Keep the roving tab stop on the selected option so that, if the list stays
    // visible, Tab/Arrow navigation resumes from the chosen bakery.
    if (matchedCard) this.#setActiveOption(matchedCard, { focus: false });

    this.#syncPickupPanel();
    this.#updateSaveState();
    if (this.#shouldShowPendingCurrentSelectionPreview()) {
      this.#renderDrawerCurrentSelectionFromPending();
      this.#setCurrentSelectionHidden(false);
    }
  }

  changeBakery(event) {
    event.preventDefault();
    this.isChangingBakery = true;
    this.pendingSelectionId = '';
    this.pendingSelection = null;
    this.pendingPickupDay = '';
    this.pendingPickupSlot = '';
    this.#setCurrentSelectionHidden(true);
    this.#applySearchStageVisibility(true);
    this.#setResultsHidden(true);
    this.refs.resultsContainer.innerHTML = '';
    if (this.refs.searchInput instanceof HTMLInputElement) this.refs.searchInput.value = '';
    this.#resetSearchFeedback();
    this.#syncPickupPanel();
    this.#updateSaveState();
    this.refs.searchInput.focus();
    this.#ensureLocationsLoaded();
  }

  selectPickupDay(day, event) {
    if (!this.pendingSelectionId) return;
    const requested = day === 'tomorrow' ? 'tomorrow' : 'today';
    const state = this.#computePickupDayState(this.pendingSelection, requested);
    if (!state.available || !state.hasFutureSlots) return;
    this.pendingPickupDay = requested;
    this.pendingPickupSlot = '';
    this.#syncPickupPanel();
    this.#updateSaveState();
  }

  #handlePickupDayChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (
      !target.classList.contains('store-selector-drawer__pickup-day-input') &&
      !target.classList.contains('store-selector-drawer__pickup-day-radio')
    ) return;
    this.selectPickupDay(target.value);
  };

  handlePickupSlotChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    this.pendingPickupSlot = target.value || '';
    this.#updateSaveState();
  }

  saveSelection(event) {
    event.preventDefault();
    void this.#attemptSaveWithPickupGuard();
  }

  async #attemptSaveWithPickupGuard() {
    if (!this.#isReadyToSave() || !this.pendingSelection) return;
    if (!this.#isPickupSelectionReady()) {
      this.#scrollToPickupSection();
      return;
    }
    await this.#persistSelection();
  }

  async #persistSelection() {
    if (!this.pendingSelection) return;
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.setSelection !== 'function' || typeof stateApi.getSelection !== 'function') return;

    const built = this.#buildSelectionDetails(this.pendingSelection);
    const internalBakeryId = LocationsService.extractInternalBakeryId(this.pendingSelection);
    const payload = {
      // Shopify Location GID, and the key checkout matches on: it becomes the
      // `amb_store_id` cart attribute that the pickup-location-selector function
      // resolves against the location catalog. The generator is fail-closed, so
      // if this is ever empty checkout offers no pickup option at all.
      storeId: this.pendingSelection.id,
      slug: this.pendingSelection.slug || LocationsService.extractShortId(this.pendingSelection.id),
      name: this.pendingSelection.name,
      // MUST be the raw Shopify Location name only (transformLocation's
      // `locationName` = `node.name`). NEVER fall back to `.name`, which is the
      // `amb.public_store_name` metafield display override — that would leak a
      // metafield value into `amb_location_name`. No longer the checkout matching
      // key (see `storeId`); it only feeds the superseded pickup-location-filter
      // function, which is the rollback path and fails open when this is empty.
      locationName: this.pendingSelection.locationName || '',
      brand: this.dataset.brand,
      countryCode: this.dataset.countryCode,
      pickupDay: this.pendingPickupDay || 'today',
      pickupSlot: this.pendingPickupSlot,
      updatedAt: new Date().toISOString(),
      distanceKm: built && Number.isFinite(built.distanceKm) ? built.distanceKm : null,
      address:
        built && built.address && typeof built.address === 'object' ? built.address : null,
      coordinates:
        built && built.coordinates && typeof built.coordinates === 'object'
          ? built.coordinates
          : null,
    };
    if (internalBakeryId) payload.internalBakeryId = internalBakeryId;

    // Bakery IANA timezone (amb.timezone metafield) — availability.js uses it
    // for the MF36 inventory-enforcement window (11:00am–9:00pm local time).
    const selectionTimezone = String(
      this.pendingSelection.metafields?.timezone || this.dataset.defaultTimezone || ''
    ).trim();
    if (selectionTimezone) payload.timezone = selectionTimezone;

    // Deliverect arrival URL (custom.mf35_location_deliverect_url) — synced to
    // the `amb_deliverect_url` cart attribute so the order-confirmation email
    // can render "Notify staff you've arrived" instead of "View your order".
    const selectionDeliverectUrl = String(
      this.pendingSelection.metafields?.mf35_location_deliverect_url || ''
    ).trim();
    if (selectionDeliverectUrl) payload.deliverectUrl = selectionDeliverectUrl;

    const previousSelection = stateApi.getSelection();
    // Only the bakery and day affect cart contents (store inventory + MF20 caps); pickup-slot edits
    // don't need the cart-clear confirm, and a first-ever selection has no cart to clear.
    const hadPreviousSelection = Boolean(previousSelection?.storeId);
    const isStoreChange =
      hadPreviousSelection && previousSelection.storeId !== payload.storeId;
    const isDayChange =
      hadPreviousSelection &&
      String(previousSelection.pickupDay || '') !== String(payload.pickupDay || '');
    const requiresConfirmation = isStoreChange || isDayChange;

    if (requiresConfirmation) {
      // Only mention cart clearing when there's actually something to clear; an empty cart gets the
      // shorter "would you like to update?" prompt so we don't warn about a no-op.
      const message = this.#hasCartItems()
        ? this.copy.confirmMetafieldUpdateChangeWithCart || this.copy.confirmMetafieldUpdateChange
        : this.copy.confirmMetafieldUpdateChange;
      const confirmed = await this.#showConfirmDialog(message);
      if (!confirmed) return;
    }

    const saved = stateApi.setSelection(payload);
    if (!saved) return;

    this.#setSearchFeedback(this.copy.saveSuccess);
    this.#syncSelectionDisplay();
    this.close();

    void this.#runSelectionSideEffects(saved, previousSelection);
  }

  /**
   * Entry CTA flow only: pending store + pickup UI (no LS / MF10 until footer Save).
   */
  #focusPendingPickupAfterStoreSelect() {
    if (!this.pendingSelection) return;
    this.isChangingBakery = false;
    this.#syncSelectionDisplay();
    this.#refreshResultsCurrentBadge();
    this.#setResultsHidden(true);
    this.#scrollToPickupSection();
  }

  async #runSelectionSideEffects(selection, previousSelection) {
    if (!selection) return;
    try {
      // Bakery (inventory + MF20 caps) and pickup day (lead time) both invalidate the existing
      // cart contents; slot-only edits do not.
      const hadPreviousSelection = Boolean(previousSelection?.storeId);
      const hasChangedStore =
        hadPreviousSelection && previousSelection.storeId !== selection.storeId;
      const hasChangedDay =
        hadPreviousSelection &&
        String(previousSelection.pickupDay || '') !== String(selection.pickupDay || '');
      if (hasChangedStore || hasChangedDay) {
        await this.#clearCartForSelectionChange();
      }
      await syncCartAttributes(selection);
      await this.#syncCustomerMetafield(selection, previousSelection);
    } catch (_) {
      // non-fatal: keep selection save flow resilient
    }
  }

  async #clearCartForSelectionChange() {
    const cleared = await clearCartSilently();
    if (cleared) {
      document.dispatchEvent(
        new CartUpdateEvent({}, 'store-selector-drawer', {
          source: 'store-selector-drawer',
          itemCount: 0,
        })
      );
    }
  }

  /**
   * After an order is placed Shopify hands the next page load a fresh empty cart with no
   * `cart.attributes`, so the customer's selected bakery / pickup window is missing from the
   * cart used for their next order even though `amb_store_selection` is still in localStorage.
   *
   * Compare the server-rendered snapshot of `cart.attributes.amb_store_id` (passed in via
   * `data-cart-amb-store-id`) with the LS selection's `storeId`. If they diverge — empty cart
   * after checkout, cross-device LS sync, etc. — re-post the full attribute set so the next
   * checkout carries the same bakery / pickup window that the SSD trigger label is showing.
   *
   * Silent: does not dispatch `cart:update` (no UI change), tolerates fetch failures (the next
   * SSD save still re-syncs), and is a no-op when LS has no valid selection.
   */
  async #ensureCartAttributesMatchSelection() {
    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.getSelection !== 'function') return;
    const selection = stateApi.getSelection();
    if (!selection?.storeId) return;

    const cartStoreId = String(this.dataset.cartAmbStoreId || '').trim();
    const selectionStoreId = String(selection.storeId || '').trim();
    if (cartStoreId === selectionStoreId) return;

    try {
      const synced = await syncCartAttributes(selection);
      if (synced) {
        this.dataset.cartAmbStoreId = selectionStoreId;
      }
    } catch (_) {
      // non-fatal: next SSD save will re-attempt the sync.
    }
  }

  async #syncCustomerMetafield(selection, _previousSelection) {
    if (!selection) return;
    if (String(this.dataset.customerLoggedIn || '') !== 'true') return;

    const baseEndpoint = String(this.dataset.customerMetafieldEndpoint || '').trim();
    if (!baseEndpoint) return;
    const endpoint = appendStoreSelectorBrandParam(
      baseEndpoint,
      resolveStoreSelectorBrand({ brand: this.dataset.brand, countryCode: this.dataset.countryCode })
    );

    const storePayload = buildMf10StorePayload(selection);
    if (!storePayload?.storeId) return;

    const ok = await postMf10CustomerMetafield(endpoint, storePayload);
    if (!ok) this.#setSearchFeedback(this.copy.metafieldUpdateFailed);
  }

  #parseCustomerMf10ScriptPayload(rawText) {
    const text = typeof rawText === 'string' ? rawText.trim() : '';
    if (!text) return null;

    let data;
    try {
      data = JSON.parse(text);
    } catch (_) {
      return null;
    }

    if (typeof data === 'string') {
      const inner = data.trim();
      if (inner.startsWith('{') || inner.startsWith('[')) {
        try {
          data = JSON.parse(inner);
        } catch (_) {
          return { storeId: inner };
        }
      } else {
        return { storeId: inner };
      }
    }

    if (!data || typeof data !== 'object') return null;
    const storeId = String(data.storeId || '').trim();
    if (!storeId) return null;
    return data;
  }

  #stripSelectionForCustomerMf10SyncCompare(sel) {
    if (!sel || typeof sel !== 'object') return null;
    return {
      storeId: String(sel.storeId || ''),
      slug: String(sel.slug || ''),
      name: String(sel.name || ''),
      brand: String(sel.brand || '').toLowerCase(),
      countryCode: String(sel.countryCode || '').toUpperCase(),
      pickupDay: String(sel.pickupDay || ''),
      pickupSlot: String(sel.pickupSlot || ''),
      internalBakeryId: String(sel.internalBakeryId || ''),
      // Included so a stale pre-timezone LS row is backfilled by the MF10
      // hydration path (which recovers timezone from the catalog) instead of
      // being skipped as "already matching".
      timezone: String(sel.timezone || ''),
      // Same backfill rationale for the Deliverect arrival URL (MF35) —
      // recovered from the catalog by the MF10 hydration path.
      deliverectUrl: String(sel.deliverectUrl || ''),
    };
  }

  #selectionMatchesCustomerMf10Sync(current, next) {
    try {
      return (
        JSON.stringify(this.#stripSelectionForCustomerMf10SyncCompare(current)) ===
        JSON.stringify(this.#stripSelectionForCustomerMf10SyncCompare(next))
      );
    } catch (_) {
      return false;
    }
  }

  async #syncSelectionFromCustomerMf10() {
    if (String(this.dataset.customerLoggedIn || '') !== 'true') return;

    // MF10 is a first-run hydrator only: it seeds a bakery for a logged-in
    // shopper who has none yet (new device, cleared storage), and never
    // overrides one they already have.
    //
    // It used to overwrite the localStorage selection on every login, which
    // then propagated into the cart via #ensureCartAttributesMatchSelection().
    // That made the saved bakery silently beat the one the shopper actually
    // picked this session — they would log in at or near checkout and collect
    // from a different bakery than the storefront had been showing them.
    //
    // `amb_store_id` is the sole authority for the checkout pickup location
    // (the pickup-location-selector function reads nothing else), so anything
    // that rewrites it behind the shopper's back has to go. Bail out if the
    // cart already carries a bakery, or if one is already stored locally.
    if (String(this.dataset.cartAmbStoreId || '').trim() !== '') return;

    const script = this.querySelector('script[type="application/json"][data-customer-mf10-payload]');
    const raw = script?.textContent?.trim() || '';
    if (!raw) return;

    const mf10 = this.#parseCustomerMf10ScriptPayload(raw);
    if (!mf10?.storeId) return;

    const pageBrand = String(this.dataset.brand || '').trim().toLowerCase();
    const pageCountry = String(this.dataset.countryCode || '').trim().toUpperCase();
    if (!pageBrand || !pageCountry) return;

    if (String(mf10.brand ?? '').trim() !== '') {
      const mfBrand = canonicalStoreSelectionBrand(mf10.brand);
      if (mfBrand !== pageBrand) return;
    }
    if (mf10.countryCode && String(mf10.countryCode).trim().toUpperCase() !== pageCountry) return;

    const stateApi = window.StoreSelectionState;
    if (!stateApi || typeof stateApi.getSelection !== 'function' || typeof stateApi.setSelection !== 'function') return;

    await this.#ensureLocationsLoaded();

    const current = stateApi.getSelection();
    // Second half of the hydrate-only-when-empty gate above. The cart check
    // uses a server-rendered snapshot, so it can be empty while localStorage
    // already holds a bakery — a shopper who picked one but has not had the
    // attributes posted yet, or whose cart was emptied after an order.
    if (current?.storeId) return;

    const next = buildAmbStoreSelectionFromMf10(mf10, {
      pageBrand,
      pageCountry,
      // Nothing to carry forward: this path only runs when no selection exists,
      // so there is no prior pickup day/slot to preserve.
      pickupDay: '',
      pickupSlot: '',
    });
    if (!next) return;

    const catalogLocation = this.#findLocationByStoreId(mf10.storeId);
    const ibFromCatalog = LocationsService.extractInternalBakeryId(catalogLocation);
    if (ibFromCatalog) next.internalBakeryId = ibFromCatalog;

    // MF10 doesn't persist the bakery timezone — recover it from the catalog
    // (same pattern as internalBakeryId) so availability.js can compute the
    // MF36 inventory-enforcement window in local bakery time.
    const tzFromCatalog = String(
      catalogLocation?.metafields?.timezone || this.dataset.defaultTimezone || ''
    ).trim();
    if (tzFromCatalog) next.timezone = tzFromCatalog;

    // MF10 doesn't persist the Deliverect arrival URL (MF35) — recover it from
    // the catalog (same pattern as timezone) so `amb_deliverect_url` stays
    // populated for logged-in shoppers hydrated from MF10.
    const deliverectUrlFromCatalog = String(
      catalogLocation?.metafields?.mf35_location_deliverect_url || ''
    ).trim();
    if (deliverectUrlFromCatalog) next.deliverectUrl = deliverectUrlFromCatalog;

    // MF10 doesn't persist the raw Shopify Location name, so recover it from the
    // catalog by storeId (same pattern as internalBakeryId above) — keeps
    // `amb_location_name` populated for logged-in shoppers hydrated from MF10.
    const locationNameFromCatalog =
      catalogLocation && typeof catalogLocation.locationName === 'string'
        ? catalogLocation.locationName.trim()
        : '';
    if (locationNameFromCatalog) next.locationName = locationNameFromCatalog;

    // No `updatedAt` carry-forward: this path only runs with no prior selection,
    // so there is no earlier pickup timestamp that a fresh stamp could misdate.

    if (this.#selectionMatchesCustomerMf10Sync(current, next)) return;

    stateApi.setSelection(next);
  }

  #syncExternalTriggerLabels(text) {
    const root = document.getElementById('store-selector-drawer-root');
    if (root !== this) return;
    const nodes = document.querySelectorAll('[data-ssd-header-trigger-label]');
    for (const node of nodes) {
      if (node === this.refs.triggerLabel) continue;
      this.#setLabelText(node, text);
    }
  }

  #isPickupSelectionReady() {
    return Boolean(this.pendingSelection && this.pendingPickupDay && this.pendingPickupSlot);
  }

  #buildSelectionDetails(store) {
    if (!store || typeof store !== 'object') return null;
    return {
      id: store.id,
      slug: store.slug || LocationsService.extractShortId(store.id),
      name: store.name,
      distanceKm: Number.isFinite(store.distanceKm) ? store.distanceKm : null,
      status: store.status || null,
      address: store.address || null,
      coordinates: store.coordinates || null,
    };
  }

  /**
   * Sync check for whether the cart currently holds at least one line item. Used to decide whether
   * the confirm dialog needs to warn about cart clearing. Reads from the live `<cart-icon>` first
   * (always in sync with the visible header bubble), then the session-storage mirror that
   * `cart-icon.js` writes on every cart update. Falls back to `true` when neither signal is
   * available so we never silently hide the warning on a real cart.
   *
   * @returns {boolean}
   */
  #hasCartItems() {
    const cartIcon = document.querySelector('cart-icon');
    if (cartIcon && typeof cartIcon.currentCartCount === 'number') {
      return cartIcon.currentCartCount > 0;
    }
    try {
      const raw = sessionStorage.getItem('cart-count');
      if (raw) {
        const parsed = JSON.parse(raw);
        const value = parseInt(parsed?.value ?? '0', 10);
        if (Number.isFinite(value)) return value > 0;
      }
    } catch (_) {
      // ignore — fall through to the safe default
    }
    return true;
  }

  /**
   * Open the styled confirm dialog on top of the SSD and resolve when the user picks an action.
   * Native `<dialog>.showModal()` centers via `:modal` UA styles + our brand CSS.
   * @param {string} message
   * @returns {Promise<boolean>} `true` if the user confirms, `false` otherwise.
   */
  #showConfirmDialog(message) {
    const dialog = this.refs.confirmDialog;
    if (!(dialog instanceof HTMLDialogElement)) return Promise.resolve(true);

    if (typeof this.confirmResolver === 'function') {
      this.confirmResolver(false);
      this.confirmResolver = null;
    }

    if (this.refs.confirmDialogMessage instanceof HTMLElement) {
      this.refs.confirmDialogMessage.textContent = message || '';
    }

    return new Promise((resolve) => {
      this.confirmResolver = resolve;
      if (!dialog.open) dialog.showModal();
      // Move focus into the dialog itself (tabindex="-1") rather than a button.
      // `showModal()` would otherwise auto-focus the first focusable button,
      // painting a prominent focus ring on it as soon as the popup opens (BDH
      // UATA-073). Focusing the dialog keeps the modal keyboard/AT accessible
      // (Esc + focus containment still work, the message is announced via
      // aria-labelledby) without highlighting a destructive default action.
      requestAnimationFrame(() => {
        if (dialog instanceof HTMLDialogElement) {
          dialog.focus();
        }
      });
    });
  }

  #resolveConfirmDialog(accepted) {
    const dialog = this.refs.confirmDialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    if (typeof this.confirmResolver === 'function') {
      const resolve = this.confirmResolver;
      this.confirmResolver = null;
      resolve(Boolean(accepted));
    }
  }

  acceptConfirmDialog() {
    this.#resolveConfirmDialog(true);
  }

  cancelConfirmDialog() {
    this.#resolveConfirmDialog(false);
  }

  /** Esc key triggers the native `cancel` event on `<dialog>`; treat as Cancel. */
  #handleConfirmDialogCancel = (event) => {
    event.preventDefault();
    this.#resolveConfirmDialog(false);
  };

  /** Click on the backdrop (outside `.store-selector-drawer__confirm-inner`) cancels. */
  #handleConfirmDialogBackdropClick = (event) => {
    if (event.target === this.refs.confirmDialog) {
      this.#resolveConfirmDialog(false);
    }
  };

  /**
   * Stop Escape from bubbling to the SSD host while the confirm popup is open.
   *
   * The SSD extends `DialogComponent`, which binds a `keydown` listener on the host element that
   * `preventDefault()`s Escape and closes the outer drawer. That bubble-up listener fires before
   * the browser's native `<dialog>` Escape semantics, so without this guard the outer drawer
   * closes and the native `cancel` event on the confirm popup is suppressed — leaving the popup
   * stranded on screen. We `stopPropagation()` (not `preventDefault()`) so the browser still fires
   * `cancel` on the topmost dialog, which `#handleConfirmDialogCancel` handles.
   *
   * @param {KeyboardEvent} event
   */
  #handleConfirmDialogKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!(this.refs.confirmDialog instanceof HTMLDialogElement) || !this.refs.confirmDialog.open) return;
    event.stopPropagation();
  };

  #scrollToPickupSection() {
    if (this.refs.pickupPanel.hidden) {
      this.refs.pickupPanel.hidden = false;
    }

    if (this.refs.pickupSlotSelect instanceof HTMLSelectElement) {
      window.setTimeout(() => {
        this.refs.pickupSlotSelect.focus();
      }, 250);
    }
  }

  #syncPickupPanel() {
    this.#syncPickupDayDateLabels();

    const hasPendingStore = Boolean(this.pendingSelectionId && this.pendingSelection);
    this.refs.pickupPanel.hidden = !hasPendingStore;
    if (!hasPendingStore) {
      this.#syncPickupUnavailableMessage(true, true);
      return;
    }

    const todayState = this.#computePickupDayState(this.pendingSelection, 'today');
    const tomorrowState = this.#computePickupDayState(this.pendingSelection, 'tomorrow');
    const todayUsable = todayState.available && todayState.hasFutureSlots;
    const tomorrowUsable = tomorrowState.available && tomorrowState.hasFutureSlots;

    this.#syncPickupUnavailableMessage(todayUsable, tomorrowUsable);

    // Aligns with bakery spec: neither day has a bookable hourly slot → default selection is Today (disabled UI).
    if (!todayUsable && !tomorrowUsable) {
      this.pendingPickupDay = 'today';
      this.pendingPickupSlot = '';
    } else if (this.pendingPickupDay === 'today' && !todayUsable && tomorrowUsable) {
      this.pendingPickupDay = 'tomorrow';
      this.pendingPickupSlot = '';
    } else if (this.pendingPickupDay === 'tomorrow' && !tomorrowUsable && todayUsable) {
      this.pendingPickupDay = 'today';
      this.pendingPickupSlot = '';
    } else if (!this.pendingPickupDay && todayUsable) {
      this.pendingPickupDay = 'today';
    } else if (!this.pendingPickupDay && tomorrowUsable) {
      this.pendingPickupDay = 'tomorrow';
    }

    const dayButtons = this.refs.pickupPanel.querySelectorAll('[data-pickup-day]');
    for (const button of dayButtons) {
      if (!(button instanceof HTMLElement)) continue;
      const dayKey = button.dataset.pickupDay;
      const usable = dayKey === 'today' ? todayUsable : tomorrowUsable;
      const active = usable && dayKey === this.pendingPickupDay;
      button.classList.toggle('store-selector-drawer__pickup-day-button--active', active);
      button.classList.toggle('store-selector-drawer__pickup-day-button--disabled', !usable);
      button.setAttribute('aria-checked', String(active));
      button.setAttribute('aria-disabled', String(!usable));
      const radio = button.querySelector('.store-selector-drawer__pickup-day-input, .store-selector-drawer__pickup-day-radio');
      if (radio instanceof HTMLInputElement) {
        radio.checked = active;
        radio.disabled = !usable;
      }
    }

    this.refs.pickupSlotSelect.disabled = !todayUsable && !tomorrowUsable;

    this.#renderPickupSlots();
  }

  /**
   * Show the bakery-closed message when MF05→MF04→MF03 yields no bookable hour for either day.
   * Also flips `data-unavailable` on the pickup panel so CSS can hide the day/slot body and
   * Save footer — the message stands alone. Save stays disabled via `#isReadyToSave` anyway.
   * @param {boolean} todayUsable
   * @param {boolean} tomorrowUsable
   */
  #syncPickupUnavailableMessage(todayUsable, tomorrowUsable) {
    const message = this.refs.pickupUnavailableMessage;
    const panel = this.refs.pickupPanel;
    const showMessage = !todayUsable && !tomorrowUsable;
    if (panel instanceof HTMLElement) {
      panel.dataset.unavailable = showMessage ? 'true' : 'false';
    }
    if (!(message instanceof HTMLElement)) return;
    message.textContent = showMessage ? this.copy.pickupBothDaysUnavailable || '' : '';
    message.hidden = !showMessage;
    message.classList.toggle('hidden', !showMessage);
  }

  #renderPickupSlots() {
    const placeholder = this.refs.pickupSlotSelect.querySelector('option')?.textContent || 'Select time slot';
    this.refs.pickupSlotSelect.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    this.refs.pickupSlotSelect.appendChild(placeholderOption);

    const slots = this.#buildPickupSlots();
    for (const slot of slots) {
      const option = document.createElement('option');
      option.value = slot.value;
      option.textContent = slot.label;
      if (slot.value === this.pendingPickupSlot) option.selected = true;
      this.refs.pickupSlotSelect.appendChild(option);
    }

    if (!slots.some((slot) => slot.value === this.pendingPickupSlot)) {
      this.pendingPickupSlot = '';
      this.refs.pickupSlotSelect.value = '';
    }
  }

  #buildPickupSlots() {
    if (!this.pendingSelection || !this.pendingPickupDay) return [];
    const state = this.#computePickupDayState(this.pendingSelection, this.pendingPickupDay);
    if (!state.available || !state.hasFutureSlots) return [];

    let openMinutes = this.#toTotalMinutes(state.open);
    let closeMinutes = this.#closeTimeToSlotFittingMinutes(state.close);
    if (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes)) return [];
    if (closeMinutes <= openMinutes) closeMinutes += 24 * 60;

    let slotStartMinutes = openMinutes;
    if (this.pendingPickupDay === 'today') {
      slotStartMinutes = this.#earliestPickupSlotStartMinutes(
        this.pendingSelection,
        'today',
        openMinutes
      );
    }

    const slots = [];
    for (let start = slotStartMinutes; start + 60 <= closeMinutes; start += 60) {
      const end = start + 60;
      const startTime = this.#minutesToTimeString(start);
      const endTime = this.#minutesToTimeString(end);
      const rangeValue = `${startTime}-${endTime}`;
      slots.push({
        value: rangeValue,
        label: this.#formatTimeRangeLabel(startTime, endTime),
      });
    }

    return slots;
  }

  /**
   * Today/tomorrow pickup window for slots (delegates to `LocationsService.resolveAvailabilityForDate`).
   * Priority spec unchanged — see `LocationsService.resolveAvailabilityForDate` JSDoc.
   */
  #resolveDayAvailability(store, pickupDay) {
    if (!store) {
      return { available: false, open: null, close: null, source: 'none' };
    }
    return LocationsService.resolveAvailabilityForDate(
      store.metafields,
      this.#getPickupDate(store, pickupDay)
    );
  }

  /** SSD search row status — delegates to `LocationsService.calculatePickupAlignedStoreStatus`. */
  #pickupAlignedStoreStatus(store) {
    if (typeof this.locationsService?.calculatePickupAlignedStoreStatus !== 'function') {
      return store.status && typeof store.status === 'object' ? store.status : {};
    }
    return this.locationsService.calculatePickupAlignedStoreStatus(store.metafields);
  }

  #computePickupDayState(store, pickupDay) {
    const availability = this.#resolveDayAvailability(store, pickupDay);
    if (!availability.available) return { ...availability, hasFutureSlots: false };

    const openMin = this.#toTotalMinutes(availability.open);
    let closeMin = this.#closeTimeToSlotFittingMinutes(availability.close);
    if (!Number.isFinite(openMin) || !Number.isFinite(closeMin)) {
      return { ...availability, available: false, hasFutureSlots: false };
    }
    if (closeMin <= openMin) closeMin += 24 * 60;

    const slotLength = 60;
    const slotStart = this.#earliestPickupSlotStartMinutes(store, pickupDay, openMin);

    return { ...availability, hasFutureSlots: slotStart + slotLength <= closeMin };
  }

  /**
   * Same “next hour boundary” math for today as in `#buildPickupSlots` (bakery hourly spec).
   * @private
   */
  #earliestPickupSlotStartMinutes(store, pickupDay, openMin) {
    if (pickupDay !== 'today') return openMin;
    const now = this.#getCurrentStoreTime(store);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const step = 60;
    const ceilStep = Math.ceil(nowMin / step) * step;
    return Math.max(openMin, ceilStep);
  }

  #pickInitialPickupDay(store, currentDay) {
    if (!store) return currentDay || 'today';
    const todayState = this.#computePickupDayState(store, 'today');
    const tomorrowState = this.#computePickupDayState(store, 'tomorrow');
    const todayUsable = todayState.available && todayState.hasFutureSlots;
    const tomorrowUsable = tomorrowState.available && tomorrowState.hasFutureSlots;

    if (currentDay === 'today' && todayUsable) return 'today';
    if (currentDay === 'tomorrow' && tomorrowUsable) return 'tomorrow';
    if (todayUsable) return 'today';
    if (tomorrowUsable) return 'tomorrow';
    return 'today';
  }

  #getPickupDate(store, pickupDay) {
    const baseDate = this.#getCurrentStoreTime(store);
    if (pickupDay === 'tomorrow') baseDate.setDate(baseDate.getDate() + 1);
    return baseDate;
  }

  #getCurrentStoreTime(store) {
    const timezone = store?.metafields?.timezone || this.dataset.defaultTimezone;
    if (this.locationsService) return this.locationsService.getCurrentTime(timezone);
    return this.#nowInTimezone(timezone);
  }

  #nowInTimezone(timezone) {
    const tz = String(timezone || 'UTC').trim() || 'UTC';
    const now = new Date();
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(now);
      const year = parts.find((p) => p.type === 'year')?.value;
      const month = parts.find((p) => p.type === 'month')?.value;
      const day = parts.find((p) => p.type === 'day')?.value;
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;
      const second = parts.find((p) => p.type === 'second')?.value;
      if (!year || !month || !day || hour == null || minute == null || second == null) return new Date();
      return new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10),
        parseInt(second, 10)
      );
    } catch (_) {
      return new Date();
    }
  }

  #syncPickupDayDateLabels() {
    const todayDate = this.#getPickupDate(this.pendingSelection, 'today');
    const tomorrowDate = this.#getPickupDate(this.pendingSelection, 'tomorrow');
    const dayButtons = this.refs.pickupPanel.querySelectorAll('[data-pickup-day]');
    for (const button of dayButtons) {
      if (!(button instanceof HTMLElement)) continue;
      const dayKey = button.dataset.pickupDay;
      const dateNode = button.querySelector('[data-pickup-day-date]');
      if (!(dateNode instanceof HTMLElement)) continue;
      dateNode.textContent = this.#formatPickupDate(dayKey === 'tomorrow' ? tomorrowDate : todayDate);
    }
  }

  #toTotalMinutes(value) {
    const [h, m] = String(value).split(':');
    const hours = Number(h);
    const minutes = Number(m);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  }

  /**
   * Close-time → minutes for slot fitting only (not for badge / status text).
   *
   * MF04 (and MF03 fallback) widely use `"23:59"` as the "closes at midnight"
   * sentinel. The slot grid emits hour-aligned 60-min blocks, so the strict
   * `slotStart + 60 <= closeMin` check rejects the 23:00–00:00 slot by exactly
   * one minute when close = `23:59` (1439). That greys out **Today** even
   * though the bakery is still open and within MF04 hours — see e.g. Lakeshore
   * (Oakville) at 22:53 EDT with MF04 Tue close `"23:59"`.
   *
   * Treat `"23:59"` as `24:00` (1440) here so the final hour is bookable.
   * Badge / `closes_at` continues to read `"11:59pm"` because those flow
   * through `LocationsService.resolveAvailabilityForDate` → `formatNextStateChange`
   * which still sees the raw close string. Without this scope limit, normalising
   * close → 24:00 upstream renders as "12:00pm" (noon) since
   * `formatNextStateChange` does `hour % 12 || 12` on `endHour=24`.
   *
   * @param {string} closeStr `"HH:MM"` from MF04 / MF03
   * @returns {number} minutes since midnight, with `23:59 → 1440`
   */
  #closeTimeToSlotFittingMinutes(closeStr) {
    const closeMin = this.#toTotalMinutes(closeStr);
    if (closeMin === 23 * 60 + 59) return 24 * 60;
    return closeMin;
  }

  #formatTimeLabel(value) {
    const [hoursRaw, minutes] = String(value).split(':');
    const hours = Number(hoursRaw);
    if (!Number.isFinite(hours)) return value;
    const period = hours >= 12 ? 'pm' : 'am';
    const display = hours % 12 || 12;
    return `${display}:${minutes}${period}`;
  }

  #formatTimeRangeLabel(start, end) {
    return `${this.#formatTimeLabel(start)} - ${this.#formatTimeLabel(end)}`;
  }

  #formatPickupDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`;
  }

  #minutesToTimeString(totalMinutes) {
    const normalized = totalMinutes % (24 * 60);
    const hh = `${Math.floor(normalized / 60)}`.padStart(2, '0');
    const mm = `${normalized % 60}`.padStart(2, '0');
    return `${hh}:${mm}`;
  }

  #findLocationByStoreId(storeId) {
    if (!storeId) return null;
    const needle = String(storeId).trim();
    const sameId = (item) => this.#isSameStoreId(item?.id, needle);
    return (
      this.allLocations.find(sameId) ||
      this.renderedLocations.find(sameId) ||
      null
    );
  }

  #isReadyToSave() {
    if (!this.pendingSelection || !this.pendingSelectionId) return false;
    if (!this.pendingPickupDay || !this.pendingPickupSlot) return false;
    const todayState = this.#computePickupDayState(this.pendingSelection, 'today');
    const tomorrowState = this.#computePickupDayState(this.pendingSelection, 'tomorrow');
    const anyDayUsable =
      (todayState.available && todayState.hasFutureSlots) ||
      (tomorrowState.available && tomorrowState.hasFutureSlots);
    return anyDayUsable;
  }

  #updateSaveState() {
    this.refs.saveButton.disabled = !this.#isReadyToSave();
  }

  #setSearchFeedback(message) {
    if (!message) return;
  }

  #resetSearchFeedback() {
    return;
  }

  /**
   * Visible zero-result state under the search input (`ref="searchEmptyState"`).
   * Distinct from #setSearchFeedback (a deliberate no-op — result counts and
   * transient statuses aren't surfaced). Currently used for the
   * "no bakeries in this search frame" message on geocoded searches.
   * `message` renders as the display heading and `link` as the supporting line
   * beneath it, matching the empty-cart panel.
   * Built via DOM nodes (not innerHTML) so translated copy can't inject markup.
   * @param {string} message
   * @param {{ label: string, url: string, suffix?: string }} [link] optional
   *   link, with any copy that follows it in `suffix`
   * @param {{ showAll?: boolean, showJoin?: boolean }} [options] reveal one of
   *   the two actions beneath the message — the loyalty sign-up CTA (`showJoin`)
   *   or the "show all Click & Collect bakeries" fallback (`showAll`)
   */
  #showSearchMessage(message, link, { showAll = false, showJoin = false } = {}) {
    const el = this.refs.searchMessage;
    const container = this.refs.searchEmptyState;
    const action = this.refs.showAllBakeriesButton;
    const join = this.refs.joinLoyaltyLink;
    if (action instanceof HTMLElement) action.classList.toggle('hidden', !message || !showAll);
    if (join instanceof HTMLElement) join.classList.toggle('hidden', !message || !showJoin);
    // Unhidden before the copy lands so the live region is visible when it changes.
    if (container instanceof HTMLElement) container.classList.toggle('hidden', !message);
    if (!(el instanceof HTMLElement)) return;
    el.textContent = '';
    if (!message) return;

    const title = document.createElement('span');
    title.className = 'store-selector-drawer__search-message-title';
    title.textContent = message;
    el.appendChild(title);

    if (link?.label && link?.url) {
      const note = document.createElement('span');
      note.className = 'store-selector-drawer__search-message-note';
      const anchor = document.createElement('a');
      anchor.className = 'store-selector-drawer__search-message-link';
      anchor.href = link.url;
      anchor.textContent = link.label;
      note.appendChild(anchor);
      if (link.suffix) note.appendChild(document.createTextNode(` ${link.suffix}`));
      el.appendChild(note);
    }
  }

  #clearSearchMessage() {
    this.#showSearchMessage('');
  }

  /**
   * "View all click-and-collect bakeries" — the COBS fallback offered
   * when a geocoded search frame contains no bakeries. Lists the full
   * selectable catalog in the drawer rather than navigating to the store
   * locator, which also lists bakeries that don't offer Click & Collect.
   * Bakers renders the same ref as a link to its online ordering page instead,
   * so this handler is only wired up on COBS.
   * The geocoded search origin is deliberately kept so the list opens with the
   * closest bakeries to what the shopper searched for.
   * @param {Event} [event]
   */
  async showAllBakeries(event) {
    event?.preventDefault();
    await this.#ensureLocationsLoaded();
    this.#clearSearchMessage();
    // Clear the query and cancel any pending debounce so a late #runSearch
    // from the just-typed term can't wipe the full catalog we're about to show.
    // Setting .value programmatically does not fire `input`, so this won't
    // re-enter #handleSearchInput.
    if (this.searchDebounce) window.clearTimeout(this.searchDebounce);
    this.searchDebounce = null;
    if (this.refs.searchInput instanceof HTMLInputElement) this.refs.searchInput.value = '';

    const selectable = this.#selectableLocations();
    if (!selectable.length) {
      this.#setResultsHidden(true);
      this.refs.resultsContainer.innerHTML = '';
      this.#setSearchFeedback(this.copy.emptyNoResultsText);
      return;
    }

    const origin = this.searchOrigin || this.userLocation;
    const ordered = origin
      ? this.#sortWithDistance(selectable)
      : [...selectable].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    this.#renderResults(ordered);
    this.#setSearchFeedback(this.#formatResultsCount(ordered.length));
  }

  #setResultsHidden(isHidden) {
    this.refs.resultsContainer.classList.toggle('hidden', Boolean(isHidden));
  }

  #resetDrawerStateOnClose() {
    this.#setResultsHidden(true);
    this.refs.resultsContainer.innerHTML = '';
    this.#clearSearchMessage();
    // Normally consumed by `#handleDialogOpen`; reset here so a request that never
    // reached an open dialog cannot redirect focus on the next, unrelated open.
    this.pendingOpenFocusPickup = false;
    if (this.refs.searchInput instanceof HTMLInputElement) this.refs.searchInput.value = '';
    this.isChangingBakery = false;
    this.refs.searchChangeBakeryButton.classList.add('hidden');
    this.pendingSelectionId = '';
    this.pendingSelection = null;
    this.pendingPickupDay = '';
    this.pendingPickupSlot = '';
    this.refs.pickupPanel.hidden = true;
    this.refs.pickupSlotSelect.value = '';
    this.refs.pickupSlotSelect.disabled = false;
    const hasSelection = this.dataset.hasSelection === 'true';
    this.refs.searchSection.classList.toggle('hidden', hasSelection);
    this.#syncPickupDayDateLabels();
    this.#syncSelectionDisplay();
  }
}

if (!customElements.get('store-selector-drawer-component')) {
  customElements.define('store-selector-drawer-component', StoreSelectorDrawerComponent);
}
